import React from 'react';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import { SectionBlocks } from '@components/SectionBlocks';
import { Layout } from '@components/layout/Layout';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import {
  ProductPageAppPropertiesType,
  SectionBlockType,
  TopicPageAppPropertiesType,
  TopicPageAppType,
} from '@appTypes/CategoryAppType';
import { SiteStructureType } from '@appTypes/siteStructureType';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { createNorwegianDateString } from '@utils/createNorwegianDateString';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import routeConfig from '../../config/routeConfig';
import * as S from './TopicPageApp.styles';

export const TopicPageApp = ({
  data,
  appData,
  asArticleData,
}: {
  data?: SiteStructureType;
  appData?: TopicPageAppType;
  asArticleData?: TopicPageAppPropertiesType | ProductPageAppPropertiesType;
}) => {
  const contentUrl = useWindowLocation();
  const topicPageData = appData?.properties || asArticleData;
  const { getProcessedImage } = useCloudinary();
  const generatedCrumbs = useBreadcrumbs(routeConfig);
  const themeType = data?.theme;

  const hasParallaxEffect = (sectionData?: SectionBlockType[]) => {
    if (sectionData) {
      return sectionData.some((section) => section.type === 'scrollableTextBlocksWithBackgroundImages');
    }
    return false;
  };

  const isWideImageHero =
    topicPageData &&
    ['heroWithTitleAndWideImage', 'heroWithTitleAndWideAndShortImage'].includes(topicPageData?.hero?.heroVariant);

  const categoryImage = getProcessedImage(data?.image?.publicId, {
    width: 1200,
    height: 630,
  })?.toURL();

  return (
    <Layout
      title={data?.name || asArticleData?.seoConfig?.title || data?.seoConfig?.title || asArticleData?.hero?.header}
      description={data?.ingress || data?.seoConfig?.introduction || 'NAF temaside'}
      seoDescription={
        asArticleData?.seoConfig?.introduction || data?.seoConfig?.introduction || data?.ingress || 'NAF temaside'
      }
      imgUrl={categoryImage || ''}
      seoTitle={asArticleData?.seoConfig?.title || data?.seoConfig?.title || data?.name}
      isHiddenFromSearch={asArticleData?.seoConfig?.isHiddenFromSearch || data?.seoConfig?.isHiddenFromSearch}
      gtmArgs={{ page_type: 'TopicPageApp', pageCategory: data?.slug || '', contentId: data?.name || '' }}
      internalSearchMetaData={{ cardType: InternalSearchCardType.CategoryApp }}
    >
      {isWideImageHero ? (
        <S.StyledHeroBlock block={topicPageData.hero} breadCrumbs={generatedCrumbs} themeType={themeType} />
      ) : null}
      {topicPageData && (
        <S.Main>
          {!isWideImageHero && (
            <S.StyledHeroBlock
              block={topicPageData.hero}
              breadCrumbs={!topicPageData.hideBreadcrumbs ? generatedCrumbs : null}
            />
          )}
          <ErrorBoundary>
            {topicPageData.sections && (
              <S.StyledSectionContainer>
                <SectionBlocks
                  content={topicPageData.sections}
                  activateContextThemeSwitch={hasParallaxEffect(topicPageData.sections)}
                />
              </S.StyledSectionContainer>
            )}
            {topicPageData.metaDisplay &&
              (!topicPageData?.metaDisplay?.hidePublicationDate || !topicPageData?.metaDisplay?.hideEditor) && (
                <S.CenterContainer>
                  {topicPageData?.meta?.publishAt &&
                    !topicPageData.metaDisplay?.hidePublicationDate &&
                    asArticleData && <div>{createNorwegianDateString(new Date(topicPageData.meta.publishAt))}</div>}
                  {topicPageData?.meta?.editor && !topicPageData?.metaDisplay?.hideEditor && asArticleData && (
                    <div>
                      {topicPageData.meta.editor.name},{' '}
                      <S.BylineRole>{topicPageData.meta.editor.position}</S.BylineRole>
                    </div>
                  )}
                </S.CenterContainer>
              )}
            {topicPageData.metaDisplay && !topicPageData.metaDisplay?.hideForm && (
              <S.CenterContainer>
                <FeedbackSurvey contentUrl={contentUrl} />
              </S.CenterContainer>
            )}
          </ErrorBoundary>
        </S.Main>
      )}
    </Layout>
  );
};
